import { MOCK } from "../constants"
import { removeValue, retrieveValue, storeValue } from "../utils"
import accountListMock from "./mock/account/list/get.json"
import transactionListMock from "./mock/transaction/list/get.json"
import paymentRequest1Mock from "./mock/transaction/paymentrequest/3cb8747a-70f1-47ac-80e9-cf07c824b983/get.json"
import paymentRequest2Mock from "./mock/transaction/paymentrequest/a7a74427-624a-4b5d-8114-c24038e6b2f9/get.json"
import paymentRequest3Mock from "./mock/transaction/paymentrequest/a7a74427-624a-4b5d-8114-c24038e6b2f8/get.json"
import transferDetails1Mock from "./mock/transaction/074fe08d-e48a-4ce7-8490-379228c23f8c/get.json"
import transferDetails2Mock from "./mock/transaction/1ed42196-2f1e-4dcf-9940-f8df9cbd3ab4/get.json"
import tokenExchangeMock from "./mock/authentication/exchangetoken/get.json"
import personalDataMock from "./mock/verification/personaldata/get.json"
import sendOtpMock from "./mock/login/otp/post.json"
import checkOtpMock from "./mock/login/checkotp/put.json"
import authPhoneStartMock from "./mock/auth/phone/start/post.json"
import authPhoneConfirmMock from "./mock/auth/phone/confirm/put.json"
import verifyPhoneNumberMock from "./mock/verification/verifyphonenumber/post.json"
import checkVerificationMock from "./mock/verification/checkverification/get.json"
import accountLinkTokenMock from "./mock/account/linktoken/post.json"
import accountLinkMock from "./mock/account/link/post.json"
import verifyStartKycMock from "./mock/verification/startkyc/post.json"
import verifyFinishKycMock from "./mock/verification/finishkyc/put.json"
import accountAddMock from "./mock/account/add/post.json"
import accountLinkPutMock from "./mock/account/link/put.json"
import chatSignatureMock from "./mock/verification/chatSignature/get.json"
import ReimbursementListProvidersMock from "./mock/reimbursement/listproviders/get.json"
import ReimbursementListMock from "./mock/reimbursement/list/get.json"
import ReimbursementCreateMock from "./mock/reimbursement/create/post.json"
import { navigate, navigateOnSessionExpired } from "../services/NavigationService.ts"
import { Sentry } from "../utils/Sentry"

const jsonMap = {
  account: {
    list: {
      get: accountListMock,
    },
    linktoken: {
      post: accountLinkTokenMock,
    },
    link: {
      post: accountLinkMock,
      put: accountLinkPutMock,
    },
    add: {
      post: accountAddMock,
    },
  },
  authentication: {
    exchangetoken: {
      get: tokenExchangeMock,
    },
  },
  transaction: {
    list: {
      get: transactionListMock,
    },
    paymentrequest: {
      "3cb8747a-70f1-47ac-80e9-cf07c824b983": {
        get: paymentRequest1Mock,
      },
      "a7a74427-624a-4b5d-8114-c24038e6b2f9": {
        get: paymentRequest2Mock,
      },
      "a7a74427-624a-4b5d-8114-c24038e6b2f8": {
        get: paymentRequest3Mock,
      },
    },
    "074fe08d-e48a-4ce7-8490-379228c23f8c": {
      get: transferDetails1Mock,
    },
    "1ed42196-2f1e-4dcf-9940-f8df9cbd3ab4": {
      get: transferDetails2Mock,
    },
  },
  verification: {
    personaldata: {
      get: personalDataMock,
    },
    verifyphonenumber: {
      post: verifyPhoneNumberMock,
    },
    checkverification: {
      get: checkVerificationMock,
    },
    startkyc: {
      post: verifyStartKycMock,
    },
    finishkyc: {
      put: verifyFinishKycMock,
    },
    chatsignature: {
      get: chatSignatureMock,
    },
  },
  login: {
    otp: {
      post: sendOtpMock,
    },
    checkotp: {
      put: checkOtpMock,
    },
  },
  auth: {
    phone: {
      start: {
        post: authPhoneStartMock,
      },
      confirm: {
        put: authPhoneConfirmMock,
      },
    },
  },
  reimbursement: {
    listproviders: {
      get: ReimbursementListProvidersMock,
    },
    list: {
      get: ReimbursementListMock,
    },
    create: {
      post: ReimbursementCreateMock,
    },
  },
}

type FetcherOptions = {
  useAccessToken?: boolean
  disableErrorScreen?: boolean
  doNotLogOutOn401?: boolean
  /** Throw on failure without navigating or clearing session. */
  silent?: boolean
}

function getPropByPath(path: string) {
  const keys = path.split("/")
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = keys.reduce((currentObject, key): any => {
    const keyWithoutQuery = key.split("?")[0] as keyof typeof jsonMap
    return currentObject && keyWithoutQuery in currentObject
      ? currentObject[keyWithoutQuery]
      : undefined
  }, jsonMap)
  console.log("Mock", path, keys, result)
  return result
}

async function networkFetcher(uri: string, params: RequestInit, options: FetcherOptions = {}) {
  const { useAccessToken, disableErrorScreen, doNotLogOutOn401, silent } = options
  let accessToken = useAccessToken ? await retrieveValue("accessToken") : undefined
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(params.headers || {}),
  }
  if (accessToken) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    headers["Authorization"] = `Bearer ${accessToken}`
  }

  const response = await fetch(`${process.env.API_BASE_URL}${uri}`, {
    method: params.method || "GET",
    headers,
    body: params.body,
  })

  accessToken = response.headers.get("accesstoken")
  if (accessToken) {
    await storeValue("accessToken", accessToken)
  }
  const clonedResponse = response.clone()
  if (response.status === 401 && silent) {
    throw new Error(`401, Uri: ${uri}`)
  }
  if (response.status === 401 && doNotLogOutOn401) {
    navigate("GeneralError", { message: "This payment is not assigned to your account" })
    throw new Error("This payment is not assigned to your account")
  }
  if (response.status === 401) {
    removeValue("accessToken")
    navigateOnSessionExpired()
    throw new Error(`401, Logged out. Uri: ${uri}`)
  }
  if (response.status < 200 || response.status >= 300) {
    const text = await clonedResponse.text()
    let displayError = "Something went wrong"
    try {
      const parsedErrorData = JSON.parse(text)

      displayError = parsedErrorData.error.Message
    } catch (err) {
      displayError = "Something went wrong"
      Sentry.captureMessage("API error response could not be parsed", {
        level: "warning",
        extra: {
          uri,
          status: response.status,
          responseBody: text,
          parseError: err instanceof Error ? err.message : String(err),
        },
        tags: {
          source: "fetcher",
        },
      })
    }
    if (!silent && !disableErrorScreen) {
      navigate("GeneralError", { message: displayError })
    }
    throw new Error(text)
  }

  return response
}

async function mockedFetcher(uri: string, params: RequestInit): Promise<Response> {
  const json = getPropByPath(
    `${uri.toLowerCase().replace(/^\//, "")}/${params.method?.toLowerCase()}`
  )
  return new Promise((resolve) => {
    return setTimeout(() => {
      return resolve({
        ok: true,
        status: 200,
        json: async () => Promise.resolve(json),
      } as Response)
    }, 1000)
  })
}

export async function fetcher(
  uri: string,
  params: RequestInit,
  options: FetcherOptions = {}
): Promise<Response> {
  const demo = (await retrieveValue("mode")) === "demo"
  if (MOCK || demo) {
    return await mockedFetcher(uri, params)
  } else {
    return await networkFetcher(uri, params, options)
  }
}
